
import { auth } from "../firebase";
import { AdjustUsers, PointAdjustHistory, PointHistoryData } from "../model/PointData";
import { Result } from "../model/Result";
import { removeNullFields, toCamelCase } from "../utility/ConvertUtily";
import axiosCilent from "./axiosClient";

export const requestGetPointName = async (): Promise<{
  success: true;
  pointNameList: string[];
} | { success: false; error: any }> => {
  try {
    const currentUser = auth.currentUser;
    const requestBody = {
      tenantId: `${currentUser?.tenantId}`,
    };
    const response = await axiosCilent.post(`getPointNameDb`, requestBody, { timeout: 0 });
    const pointNameList = toCamelCase(removeNullFields(response.data.pointNameList));

    return {
      success: true,
      pointNameList: pointNameList,
    };
  } catch (error) {
    return { success: false, error };
  }
};

export const requestGetPointHistory = async (
  pageNo: number,
  limit: number,
  searchConditions?: any
): Promise<{
  success: true;
  pointHistory: PointHistoryData[];
  totalCount: number;
  totalPages: number;
} | { success: false; error: any }> => {
  try {
    const currentUser = auth.currentUser;
    const requestBody = {
      tenantId: `${currentUser?.tenantId}`,
      page: pageNo,
      limit: limit,
      ...searchConditions, 
    };
    const response = await axiosCilent.post(`getPointListDb`, requestBody, { timeout: 0 });
    const pointHistoryList = toCamelCase(removeNullFields(response.data.pointHistory));
    const mappedPointHistoryList: PointHistoryData[] = pointHistoryList.map((ph: any) => ({
        ...ph,
        occurrenceDate: new Date(ph.occurrenceDate),
        createAt: new Date(ph.createAt),
        updateAt: new Date(ph.updateAt),
        grantDate: ph.grantDate ? new Date(ph.grantDate) : null,
        expirationDate: ph.expirationDate ? new Date(ph.expirationDate) : null,
    }));

    return {
      success: true,
      pointHistory: mappedPointHistoryList,
      totalCount: response.data.totalCount,
      totalPages: response.data.totalPages,
    };
  } catch (error) {
    return { success: false, error };
  }
};

export const requestGetPointAdjustHistory = async (
  pageNo: number,
  limit: number
): Promise<{
  success: true;
  pointAdjustHistory: PointAdjustHistory[];
  totalCount: number;
  totalPages: number;
} | { success: false; error: any }> => {
  try {
    const currentUser = auth.currentUser;
    const requestBody = {
      tenantId: currentUser?.tenantId,
      page: pageNo,
      limit,
    };

    const response = await axiosCilent.post(`getPointAdjustHistoryDb`, requestBody, { timeout: 0 });

    const adjustList: PointAdjustHistory[] = response.data.pointAdjustHistory.map((ph: any) => ({
      id: ph.id,
      tenantId: ph.tenant_id,
      ptCategoryId: ph.pt_category_id,
      ptCategoryName: ph.pt_category_name,
      ptTypeId: ph.pt_type_id,
      ptTypeName: ph.pt_type_name,
      ptName: ph.pt_name,
      grantPoints: ph.grant_points,
      occurrenceDate: ph.occurrence_date ? new Date(ph.occurrence_date) : new Date(),
      createUser: ph.create_user,
      createAt: ph.create_at ? new Date(ph.create_at) : new Date(),
      updateAt: ph.update_at ? new Date(ph.update_at) : new Date(),
      expirationDate: ph.expiration_date ? new Date(ph.expiration_date) : undefined,
      note: ph.note ?? "",
    }));

    return {
      success: true,
      pointAdjustHistory: adjustList,
      totalCount: response.data.totalCount,
      totalPages: response.data.totalPages,
    };
  } catch (error) {
    console.error("requestGetPointAdjustHistory error:", error);
    return { success: false, error };
  }
};

export const requestGetAdjustUsers = async (
  pointAdjustmentId: string,
  pageNo: number,
  limit: number
): Promise<{
  success: true;
  adjustUsers: AdjustUsers[];
  totalCount: number;
  totalPages: number;
} | { success: false; error: any }> => {
  try {
    const currentUser = auth.currentUser;
    const requestBody = {
      pointAdjustmentId: pointAdjustmentId,
      tenantId: currentUser?.tenantId,
      page: pageNo,
      limit,
    };

    const response = await axiosCilent.post(`getAdjustUsersDb`, requestBody, { timeout: 0 });

    const adjustList: AdjustUsers[] = response.data.adjustUsers.map((u: any) => ({
      id: u.id,
      tenantId: u.tenant_id,
      pointAdjustHistoryId: u.point_adjust_history_id,
      userId: u.user_id,
      displayUserId: u.display_user_id,
      userName: u.user_name,
      groupNameArr: u.group_name_arr ?? [],
      executionResult: u.execution_result,
      errorDetail: u.error_detail ?? "",
    }));

    return {
      success: true,
      adjustUsers: adjustList,
      totalCount: response.data.totalCount,
      totalPages: response.data.totalPages,
    };
  } catch (error) {
    console.error("requestGetPointAdjustHistory error:", error);
    return { success: false, error };
  }
};

export const requestExecuteAdjust = async (
  tenantId: string,
  inputData: PointAdjustHistory,
  checkedList: string[]
): Promise<{ success: true; results?: any[] } | { success: false; error: any }> => {
  try {
    const currentUser = auth.currentUser;

    const requestBody = {
      tenantId,
      inputData,
      checkedList,
      executor: currentUser?.displayName,
    };

    const response = await axiosCilent.post(`executePointAdjust`, requestBody, { timeout: 0 });

    if (response.data?.success) {
      return { success: true, results: response.data.results };
    } else {
      return { success: false, error: response.data?.error };
    }
  } catch (error) {
    console.error("requestExecuteAdjust error:", error);
    return { success: false, error };
  }
};

export const requestEventPointAwarded = async (tenantId: string, messageId: string): Promise<Result> => {
  try {
    const response = await axiosCilent.post(`eventPointAwarded`, { tenantId, messageId });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, data: error };
  }
};